import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { X } from "lucide-react";
import type { JSX } from "react";

type OnboardingOverlaySkeletonProps = {
  onSkip?: () => void;
};

export function OnboardingOverlaySkeleton({
  onSkip,
}: OnboardingOverlaySkeletonProps): JSX.Element {
  return (
    <div className="flex h-svh flex-col bg-background md:flex-row">
      {/* Mobile top bar — mirrors OnboardingMobileTopBar (StepsProgress) */}
      <div className="flex h-14 shrink-0 items-center overflow-hidden border-border border-b md:hidden">
        {/* Below sm: carousel — single step with prev/next chevrons */}
        <div className="flex h-full w-full items-center sm:hidden">
          <div className="flex h-full w-8 shrink-0 items-center justify-center px-2">
            <Skeleton className="h-4 w-4" />
          </div>
          <div className="flex flex-1 items-center justify-center px-2 py-4">
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex h-full w-8 shrink-0 items-center justify-center px-2">
            <Skeleton className="h-4 w-4" />
          </div>
        </div>
        {/* sm–md: all steps visible side by side */}
        <div className="hidden h-full w-full items-center gap-3 px-3 sm:flex">
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 flex-1" />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden w-72 shrink-0 flex-col border-border border-r bg-sidebar px-8 pt-12 pb-4 md:flex">
        <Skeleton className="h-7 w-24" />
        <div className="mt-16 flex flex-col gap-8">
          <div className="flex items-center gap-4">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        {onSkip && (
          <div className="mt-auto">
            <Button
              className="-ml-2.5 text-muted-foreground/60 text-xs italic transition-colors hover:text-muted-foreground"
              onClick={onSkip}
              size="sm"
              type="button"
              variant="ghost"
            >
              <X className="h-3 w-3" />
              Skip setup
            </Button>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-auto px-4 pt-8 md:px-8 md:pt-12">
          <div className="mx-auto w-full max-w-5xl">
            <Skeleton className="mb-4 h-7 w-56" />
            <Skeleton className="mb-2 h-4 w-full max-w-sm" />
            <Skeleton className="mb-8 h-4 w-3/4 max-w-xs" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Skeleton className="h-36 rounded-lg sm:h-52" />
              <Skeleton className="h-36 rounded-lg sm:h-52" />
              <Skeleton className="h-36 rounded-lg sm:h-52" />
            </div>
          </div>
        </div>
        {onSkip && (
          <div className="flex shrink-0 justify-center py-4 md:hidden">
            <Button
              className="text-muted-foreground/60 text-xs italic transition-colors hover:text-muted-foreground"
              onClick={onSkip}
              size="sm"
              type="button"
              variant="ghost"
            >
              <X className="h-3 w-3" />
              Skip setup
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
